import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Flame, Trophy, Users } from "lucide-react";
import { fetchers } from "../lib/api";
import AdSlot from "../components/AdSlot";
import MatchCard from "../components/MatchCard";
import NewsCard from "../components/NewsCard";
import { StandingsTable } from "./Standings";

const Section = ({ title, kicker, right, children }) => (
  <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
    <div className="mb-6 flex items-end justify-between gap-4 border-b border-border pb-3">
      <div>
        {kicker && <div className="overline-eyebrow text-primary">{kicker}</div>}
        <h2 className="font-display text-3xl uppercase sm:text-4xl">{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </section>
);

export default function Home() {
  const { data: news } = useQuery({ queryKey: ["news-home"], queryFn: () => fetchers.news({ limit: 10 }) });
  const { data: upcoming } = useQuery({ queryKey: ["home-upcoming"], queryFn: () => fetchers.upcoming("all", 9) });
  const { data: past } = useQuery({ queryKey: ["home-past"], queryFn: () => fetchers.past("all", 9) });
  const { data: live } = useQuery({ queryKey: ["home-live"], queryFn: fetchers.live, refetchInterval: 60_000 });
  const { data: standings } = useQuery({ queryKey: ["home-standings"], queryFn: () => fetchers.standings("epl") });

  const articles = news?.items || [];
  const feature = articles[0];
  const secondary = articles.slice(1, 4);
  const grid = articles.slice(4, 10);

  const liveEvents = live?.events || [];
  const upcomingEvents = [...liveEvents, ...(upcoming?.events || [])].slice(0, 6);
  const pastEvents = (past?.events || []).slice(0, 9);
  const topTable = (standings?.table || []).slice(0, 5);

  return (
    <div className="animate-fade-up">
      {/* Hero banner ad */}
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <AdSlot size="leaderboard" slotId="hero-top" className="hidden md:flex" />
        <AdSlot size="large_mobile" slotId="hero-top-m" className="md:hidden" />
      </div>

      {/* HERO - Bento layout */}
      <section className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex items-center gap-3">
          <span className="inline-flex items-center gap-2 bg-primary px-3 py-1 overline-eyebrow text-primary-foreground">
            <Flame size={12} /> Trending
          </span>
          <span className="text-xs uppercase tracking-widest text-muted-foreground">
            Updated moments ago · Live from the pitch
          </span>
        </div>
        <div className="grid gap-5 lg:grid-cols-12">
          {feature && (
            <div className="lg:sticky lg:top-32 lg:col-span-8 lg:self-start">
              <NewsCard article={feature} size="xl" />
            </div>
          )}
          <div className="flex flex-col gap-5 lg:col-span-4">
            {secondary.map((a) => (
              <NewsCard key={a.id} article={a} size="sm" />
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming + sidebar ad */}
      <Section
        title={liveEvents.length ? "Live & Upcoming" : "Upcoming Fixtures"}
        kicker={`${liveEvents.length} live now · 20 competitions · next 7 days`}
        right={
          <Link to="/matches" className="link-underline overline-eyebrow text-primary">
            All matches →
          </Link>
        }
      >
        <div className="grid gap-6 lg:grid-cols-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-3">
            {upcomingEvents.length === 0 && (
              <div className="col-span-full py-6 text-sm text-muted-foreground">Fetching upcoming matches…</div>
            )}
            {upcomingEvents.map((ev) => (
              <MatchCard key={ev.id} event={ev} />
            ))}
          </div>
          <aside className="space-y-5 lg:col-span-1">
            <AdSlot size="rectangle" slotId="home-side-1" />
            <AdSlot size="half_page" slotId="home-side-2" className="hidden lg:flex" />
          </aside>
        </div>
      </Section>

      {/* In-article responsive ad */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <AdSlot size="billboard" slotId="home-inarticle-1" className="hidden lg:flex" />
        <AdSlot size="responsive" slotId="home-inarticle-1m" className="lg:hidden" />
      </div>

      {/* Results */}
      <Section title="Latest Results" kicker="Full-time · All competitions">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pastEvents.length === 0 && (
            <div className="col-span-full py-6 text-sm text-muted-foreground">Loading recent results…</div>
          )}
          {pastEvents.map((ev) => (
            <MatchCard key={ev.id} event={ev} />
          ))}
        </div>
      </Section>

      {/* News grid */}
      <Section
        title="The Pulse"
        kicker="Features · Opinion · Analysis"
        right={
          <Link to="/news" className="link-underline overline-eyebrow text-primary">
            Read all →
          </Link>
        }
      >
        <div className="grid gap-6 lg:grid-cols-4">
          <div className="grid gap-6 md:grid-cols-2 lg:col-span-3">
            {grid.map((a) => (
              <NewsCard key={a.id} article={a} />
            ))}
          </div>
          <aside className="space-y-5">
            <AdSlot size="rectangle" slotId="home-side-3" />
            <div className="border border-border bg-card p-5">
              <div className="overline-eyebrow text-primary">Newsletter</div>
              <h3 className="mt-1 font-display text-2xl uppercase leading-none">
                Three stories. Every morning.
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                A tight dispatch of the biggest football news, delivered before kickoff.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="mt-4 flex gap-2"
              >
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  placeholder="you@club.com"
                  className="w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  className="bg-primary px-3 py-2 overline-eyebrow text-primary-foreground hover:bg-blaze-600"
                >
                  Join
                </button>
              </form>
            </div>
            <AdSlot size="rectangle" slotId="home-side-4" />
          </aside>
        </div>
      </Section>

      {/* Standings snapshot */}
      <Section
        title="Table Snapshot"
        kicker="Premier League · Top 5"
        right={
          <Link to="/standings" className="link-underline overline-eyebrow text-primary">
            Full table →
          </Link>
        }
      >
        <div className="grid gap-6 lg:grid-cols-4">
          <div className="lg:col-span-3">
            {topTable.length === 0 ? (
              <div className="border border-border bg-card px-4 py-6 text-sm text-muted-foreground">Loading table…</div>
            ) : (
              <StandingsTable rows={topTable} compact testId="home-standings-table" />
            )}
          </div>
          <aside className="space-y-5">
            <div className="border border-border bg-card p-5">
              <div className="overline-eyebrow text-primary flex items-center gap-2">
                <Trophy size={12} /> Golden Boot Watch
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                {[
                  { n: "E. Haaland", g: 22 },
                  { n: "H. Kane", g: 20 },
                  { n: "K. Mbappé", g: 19 },
                  { n: "V. Osimhen", g: 17 },
                  { n: "L. Martinez", g: 16 },
                ].map((p, i) => (
                  <li key={p.n} className="flex items-center justify-between border-b border-border pb-1 last:border-b-0 last:pb-0">
                    <span className="flex items-center gap-2">
                      <span className="font-display text-muted-foreground">{i + 1}.</span>
                      {p.n}
                    </span>
                    <span className="font-display text-lg text-primary">{p.g}</span>
                  </li>
                ))}
              </ul>
            </div>
            <AdSlot size="rectangle" slotId="home-side-5" />
          </aside>
        </div>
      </Section>

      {/* Teams CTA */}
      <Section title="Clubs & Teams" kicker="Explore squad profiles">
        <div className="grid gap-5 md:grid-cols-3">
          <Link
            to="/teams?league=epl"
            className="card-hover group flex items-center justify-between border border-border bg-card p-6"
          >
            <div>
              <div className="overline-eyebrow text-primary">Top Flight</div>
              <div className="font-display text-3xl uppercase">Premier League Clubs</div>
            </div>
            <ArrowRight className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/teams?league=laliga"
            className="card-hover group flex items-center justify-between border border-border bg-card p-6"
          >
            <div>
              <div className="overline-eyebrow text-primary">Spain</div>
              <div className="font-display text-3xl uppercase">La Liga Squads</div>
            </div>
            <ArrowRight className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/teams?league=ucl"
            className="card-hover group flex items-center justify-between border border-border bg-card p-6"
          >
            <div>
              <div className="overline-eyebrow text-primary">Europe</div>
              <div className="font-display text-3xl uppercase">UCL Contenders</div>
            </div>
            <Users className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </Section>

      {/* Bottom banner ad */}
      <div className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8">
        <AdSlot size="billboard" slotId="home-bottom" className="hidden lg:flex" />
        <AdSlot size="responsive" slotId="home-bottom-m" className="lg:hidden" />
      </div>
    </div>
  );
}
